import { useState } from 'react';
import type { Root, Include } from './interface';
import { useDebounce } from '@simplywallst/hooks/useDebounce';
import { ALGOLIA_APP_ID, ALGOLIA_API_KEY } from '@/constants/env';
import { useQuery } from '@tanstack/react-query';
import { getAlgoliaIndex } from './utils';
export enum Status {
  IDLE,
  SEARCHING,
  ERROR,
}

export const filterHits = (hits: Root[], include: Include[]): Root[] => {
  return hits.filter((result) => {
    return include.includes(result.type);
  });
};

type Args = {
  appId?: string;
  apiKey?: string;
  include?: Include[];
};

export const useSearch = ({
  appId = ALGOLIA_APP_ID,
  apiKey = ALGOLIA_API_KEY,
  include = ['company'],
}: Args = {}) => {
  const [query, setQuery] = useState('');
  const searchIndex = getAlgoliaIndex(appId, apiKey);
  const debouncedQuery = useDebounce(query, 420);

  const searchQuery = useQuery({
    queryKey: ['ALGOLIA_SEARCH', debouncedQuery],
    queryFn: async ({ queryKey }) => {
      const { 1: q } = queryKey;
      const response = await searchIndex.search<Root>(q, {
        highlightPostTag: ' ',
        highlightPreTag: ' ',
        restrictHighlightAndSnippetArrays: true,
      });
      if (typeof response !== 'undefined') {
        return filterHits(response.hits, include);
      }
      return [];
    },
    enabled: debouncedQuery !== '',
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 3600,
  });

  let status: Status = Status.IDLE;

  if (searchQuery.fetchStatus === 'fetching') {
    status = Status.SEARCHING;
  }
  if (searchQuery.status === 'error') {
    status = Status.ERROR;
  }
  return {
    search: setQuery,
    results: searchQuery.data || [],
    status,
    query: debouncedQuery,
  };
};
